import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { ArrowLeft, ArrowRight, Ruler } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { isTwoPairSale, pairSessionKey } from '../../utils/salePairs';
import PairBanner from '../../components/PairBanner';

const Container = styled.div`
  min-height: 100vh;
  background: ${props => props.theme.colors.background};
  padding: ${props => props.theme.spacing.lg};
`;

const Header = styled.div`
  max-width: 1000px;
  margin: 0 auto;
  margin-bottom: ${props => props.theme.spacing['2xl']};
`;

const BackButton = styled.button`
  display: flex;
  align-items: center;
  gap: ${props => props.theme.spacing.sm};
  padding: ${props => props.theme.spacing.sm} ${props => props.theme.spacing.md};
  background: ${props => props.theme.colors.white};
  border: 1px solid ${props => props.theme.colors.border};
  border-radius: ${props => props.theme.borderRadius.md};
  color: ${props => props.theme.colors.textSecondary};
  cursor: pointer;
  transition: all ${props => props.theme.transitions.fast};
  margin-bottom: ${props => props.theme.spacing.lg};

  &:hover {
    background: ${props => props.theme.colors.gray50};
    color: ${props => props.theme.colors.textPrimary};
  }
`;

const Title = styled.h1`
  font-size: ${props => props.theme.fontSizes['3xl']};
  font-weight: ${props => props.theme.fontWeights.bold};
  color: ${props => props.theme.colors.textPrimary};
  margin: 0;
`;

const Form = styled.form`
  max-width: ${props => (props.$wide ? '1200px' : '1000px')};
  margin: 0 auto;
  background: ${props => props.theme.colors.white};
  border-radius: ${props => props.theme.borderRadius.lg};
  padding: ${props => props.theme.spacing['2xl']};
  box-shadow: ${props => props.theme.shadows.sm};
  border: 1px solid ${props => props.theme.colors.border};
`;

// Two-pair sale: pair 1 and pair 2's measures side by side.
const PairSections = styled.div`
  display: grid;
  grid-template-columns: ${props => (props.$two ? 'repeat(2, minmax(0, 1fr))' : 'minmax(0, 1fr)')};
  gap: ${props => props.theme.spacing.lg};

  @media (max-width: 900px) {
    grid-template-columns: minmax(0, 1fr);
  }
`;

const FormSection = styled.div`
  margin-bottom: ${props => props.theme.spacing['2xl']};
`;

const SectionTitle = styled.h3`
  font-size: ${props => props.theme.fontSizes.lg};
  font-weight: ${props => props.theme.fontWeights.semibold};
  color: ${props => props.theme.colors.textPrimary};
  margin: 0 0 ${props => props.theme.spacing.lg} 0;
  padding-bottom: ${props => props.theme.spacing.sm};
  border-bottom: 2px solid ${props => props.theme.colors.primary};
  display: flex;
  align-items: center;
  gap: ${props => props.theme.spacing.sm};
`;

const FormGroup = styled.div`
  margin-bottom: ${props => props.theme.spacing.lg};
`;

const Label = styled.label`
  display: block;
  font-size: ${props => props.theme.fontSizes.sm};
  font-weight: ${props => props.theme.fontWeights.medium};
  color: ${props => props.theme.colors.textPrimary};
  margin-bottom: ${props => props.theme.spacing.sm};
`;

const Required = styled.span`
  color: #dc2626;
  margin-left: 0.25rem;
  font-weight: ${props => props.theme.fontWeights.bold};
`;

const ErrorText = styled.span`
  display: block;
  margin-top: 0.375rem;
  font-size: 0.75rem;
  color: #dc2626;
  font-weight: 500;
`;

const Input = styled.input`
  width: 100%;
  padding: ${props => props.theme.spacing.md};
  border: 1px solid ${props => (props.$invalid ? '#dc2626' : props.theme.colors.border)};
  border-radius: ${props => props.theme.borderRadius.md};
  font-size: ${props => props.theme.fontSizes.base};
  transition: border-color ${props => props.theme.transitions.fast},
              box-shadow ${props => props.theme.transitions.fast};

  &:focus {
    outline: none;
    border-color: ${props => (props.$invalid ? '#dc2626' : props.theme.colors.primary)};
    box-shadow: 0 0 0 3px ${props => (props.$invalid ? 'rgba(220, 38, 38, 0.15)' : 'rgba(59, 130, 246, 0.15)')};
  }

  /* Hide the spinner buttons for a cleaner 2-digit input */
  &::-webkit-outer-spin-button,
  &::-webkit-inner-spin-button {
    -webkit-appearance: none;
    margin: 0;
  }
  &[type='number'] {
    -moz-appearance: textfield;
  }
`;

// Two columns: OD on the left, OG on the right (mounting height, then PD).
const MeasurementGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: ${props => props.theme.spacing.lg};
  
  @media (max-width: 768px) {
    grid-template-columns: 1fr;
  }
`;

const ButtonGroup = styled.div`
  display: flex;
  gap: ${props => props.theme.spacing.md};
  justify-content: flex-end;
  margin-top: ${props => props.theme.spacing['2xl']};
`;

// Mounting height is measured per eye (OD / OG), single vision included.
const MEASUREMENT_FIELD_KEYS = ['mountingHeightOD', 'mountingHeightOG', 'pdOD', 'pdOG'];
const isMountingKey = (key) => key.startsWith('mountingHeight');

// 2–3 digits, with an optional single decimal (half-mm precision, e.g. 32.5).
const MEASUREMENT_DIGITS_RE = /^\d{2,3}([.,]\d)?$/;
// Mounting height: exactly 2 digits, no decimal.
const MOUNTING_HEIGHT_RE = /^\d{2}$/;
const isValidMeasurementDigits = (v, key) => {
  const s = String(v ?? '').trim();
  return isMountingKey(key) ? MOUNTING_HEIGHT_RE.test(s) : MEASUREMENT_DIGITS_RE.test(s);
};

const NextButton = styled.button`
  display: flex;
  align-items: center;
  gap: ${props => props.theme.spacing.sm};
  padding: ${props => props.theme.spacing.md} ${props => props.theme.spacing.lg};
  background: ${props => props.theme.colors.primary};
  color: ${props => props.theme.colors.white};
  border: none;
  border-radius: ${props => props.theme.borderRadius.md};
  font-weight: ${props => props.theme.fontWeights.medium};
  cursor: pointer;
  transition: all ${props => props.theme.transitions.fast};

  &:hover {
    background: ${props => props.theme.colors.primaryDark};
  }
`;

const FIELD_PROPS = {
  mountingHeightOD: { pattern: '\\d{2}', maxLength: 2 },
  mountingHeightOG: { pattern: '\\d{2}', maxLength: 2 },
  pdOD: { pattern: '\\d{2,3}([.,]\\d)?', maxLength: 5 },
  pdOG: { pattern: '\\d{2,3}([.,]\\d)?', maxLength: 5 },
};
const emptyFields = (value) => Object.fromEntries(MEASUREMENT_FIELD_KEYS.map((key) => [key, value]));

const Measurement = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();

  // A two-pair sale ("Both") measures pair 1 (far) and pair 2 (close up) side
  // by side; pair 2 saves under the "Near" session keys.
  const [pairs] = useState(() => (isTwoPairSale() ? ['far', 'near'] : ['far']));
  const [loading, setLoading] = useState(false);
  const [forms, setForms] = useState(() => Object.fromEntries(pairs.map((pair) => [pair, emptyFields('')])));
  const [touched, setTouched] = useState(() => Object.fromEntries(pairs.map((pair) => [pair, emptyFields(false)])));
  const [submitAttempted, setSubmitAttempted] = useState(false);

  // Load saved measurement data on mount
  useEffect(() => {
    pairs.forEach((pair) => {
      const savedMeasurementDataStr = sessionStorage.getItem(pairSessionKey('measurementFormData', pair));
      if (!savedMeasurementDataStr) return;
      try {
        const savedData = JSON.parse(savedMeasurementDataStr);
        // A draft saved before the per-eye split had one mounting height.
        if (savedData.mountingHeight && !savedData.mountingHeightOD && !savedData.mountingHeightOG) {
          savedData.mountingHeightOD = savedData.mountingHeight;
          savedData.mountingHeightOG = savedData.mountingHeight;
        }
        setForms((prev) => {
          const next = { ...prev[pair] };
          MEASUREMENT_FIELD_KEYS.forEach((k) => {
            if (savedData[k] !== undefined && savedData[k] !== null) {
              next[k] = savedData[k];
            }
          });
          return { ...prev, [pair]: next };
        });
      } catch (err) {
        console.error('Error loading saved measurement data:', err);
      }
    });
  }, [pairs]);

  // Auto-save measurement data
  useEffect(() => {
    pairs.forEach((pair) => {
      if (Object.values(forms[pair]).some(val => val !== '')) {
        sessionStorage.setItem(pairSessionKey('measurementFormData', pair), JSON.stringify(forms[pair]));
      }
    });
  }, [forms, pairs]);

  const handleChange = (pair) => (e) => {
    const { name, value } = e.target;
    const setField = (cleaned) => setForms((prev) => ({ ...prev, [pair]: { ...prev[pair], [name]: cleaned } }));
    if (isMountingKey(name)) {
      // Mounting height: digits only, max 2 (no decimal).
      setField(value.replace(/\D/g, '').slice(0, 2));
      return;
    }
    // Allow up to 3 integer digits plus an optional single decimal (e.g. 32.5).
    const raw = value.replace(',', '.').replace(/[^\d.]/g, '');
    const [intPartRaw, ...rest] = raw.split('.');
    const intPart = intPartRaw.slice(0, 3);
    setField(rest.length > 0 ? `${intPart}.${(rest.join('') || '').slice(0, 1)}` : intPart);
  };

  const handleBlur = (pair) => (e) => {
    const { name } = e.target;
    setTouched((prev) => ({ ...prev, [pair]: { ...prev[pair], [name]: true } }));
  };

  const fieldError = (pair, key) => {
    const v = String(forms[pair][key] ?? '').trim();
    if (!v) return t('measurement.fieldRequired');
    if (!isValidMeasurementDigits(v, key)) return t('measurement.invalidFormat');
    return '';
  };

  const showError = (pair, key) => (touched[pair][key] || submitAttempted) && Boolean(fieldError(pair, key));

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitAttempted(true);

    const allValid = pairs.every((pair) => MEASUREMENT_FIELD_KEYS.every((key) => (
      isValidMeasurementDigits(String(forms[pair][key]).trim(), key)
    )));
    if (!allValid) {
      setTouched(Object.fromEntries(pairs.map((pair) => [pair, emptyFields(true)])));
      return;
    }

    setLoading(true);

    try {
      const visitorId = sessionStorage.getItem('visitorId');
      if (!visitorId) {
        throw new Error('No visitor ID found');
      }

      // Get prescription ID if available
      const prescriptionId = sessionStorage.getItem('prescriptionId');

      pairs.forEach((pair) => {
        // Get frame selection from session storage
        const selectedFrame = JSON.parse(sessionStorage.getItem(pairSessionKey('selectedFrame', pair)) || '{}');
        const form = forms[pair];

        // Save measurement data
        const measurementData = {
          mountingHeightOD: String(form.mountingHeightOD).trim(),
          mountingHeightOG: String(form.mountingHeightOG).trim(),
          pdOD: String(form.pdOD).trim(),
          pdOG: String(form.pdOG).trim(),
          visitor: visitorId,
          prescription: prescriptionId || null,
          frameSelection: selectedFrame
        };

        // Store in session storage for order confirmation
        sessionStorage.setItem(pairSessionKey('measurementData', pair), JSON.stringify(measurementData));
      });

      navigate('/payment');
    } catch (error) {
      console.error('Error saving measurements:', error);
      alert(t('measurement.saveError'));
    } finally {
      setLoading(false);
    }
  };

  // Tracking order functionality removed - use Track Order page in sidebar instead

  const renderField = (pair, key, labelKey) => {
    const id = pairs.length > 1 ? `${key}-${pair}` : key;
    return (
      <FormGroup key={key}>
        <Label htmlFor={id}>
          {t(labelKey)}<Required aria-hidden="true">*</Required>
        </Label>
        <Input
          type="text"
          inputMode="numeric"
          pattern={FIELD_PROPS[key].pattern}
          maxLength={FIELD_PROPS[key].maxLength}
          id={id}
          name={key}
          value={forms[pair][key]}
          onChange={handleChange(pair)}
          onBlur={handleBlur(pair)}
          placeholder={t('measurement.mmPlaceholder')}
          required
          $invalid={showError(pair, key)}
          aria-invalid={showError(pair, key) || undefined}
        />
        {showError(pair, key) && <ErrorText>{fieldError(pair, key)}</ErrorText>}
      </FormGroup>
    );
  };

  return (
    <Container>
      <Header>
        <BackButton onClick={() => navigate('/frame-selection')}>
          <ArrowLeft size={18} />
          {t('measurement.backToFrame')}
        </BackButton>
        <Title>{t('measurement.title')}</Title>
      </Header>

      <Form onSubmit={handleSubmit} $wide={pairs.length > 1}>
        <PairSections $two={pairs.length > 1}>
          {pairs.map((pair) => (
            <FormSection key={pair}>
              {pairs.length > 1 && (
                <PairBanner style={{ marginBottom: '0.75rem' }}>{t(pair === 'near' ? 'pairs.pairNear' : 'pairs.pairFar')}</PairBanner>
              )}
              <SectionTitle>
                <Ruler size={20} />
                {t('measurement.mountingPd')}
              </SectionTitle>

              <MeasurementGrid>
                {renderField(pair, 'mountingHeightOD', 'measurement.mountingHeightOD')}
                {renderField(pair, 'mountingHeightOG', 'measurement.mountingHeightOG')}
                {renderField(pair, 'pdOD', 'measurement.pdOD')}
                {renderField(pair, 'pdOG', 'measurement.pdOG')}
              </MeasurementGrid>
            </FormSection>
          ))}
        </PairSections>

        <ButtonGroup>
          <NextButton type="submit" disabled={loading}>
            <span>{loading ? t('common.saving') : t('measurement.completeOrder')}</span>
            <ArrowRight size={18} />
          </NextButton>
        </ButtonGroup>
      </Form>
    </Container>
  );
};

export default Measurement;

